// passiveAssetUi.js
// Small shared UI helpers for the passive-asset map views (BLE Asset Tracker
// and BLE Reader Tracker).

import React from 'react';
import L from 'leaflet';
import moment from 'moment';
import { Polygon, Tooltip as LeafletTooltip } from 'react-leaflet';

// Esri World Imagery satellite tiles (same base layer used by the Airport Maps
// project). Use maxZoom 20 for close-up airport detail.
export const ESRI_TILE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
export const ESRI_TILE_ATTRIBUTION = 'Tiles &copy; Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community';

// Render the organisation geo-fences as labelled polygons (points of interest).
export function renderGeoFencePolygons(geoFences) {
  if (!Array.isArray(geoFences)) return null;
  return geoFences.map((gf) => (gf.rings || []).map((ring, ri) => (
    <Polygon
      key={`gf-${gf.id}-${ri}`}
      positions={ring}
      color="#fadb14"
      weight={2}
      opacity={0.9}
      fillColor="#fadb14"
      fillOpacity={0.08}
    >
      <LeafletTooltip direction="center" opacity={0.9}>
        <span style={{ fontSize: 11, fontWeight: 600 }}>{gf.name}</span>
      </LeafletTooltip>
    </Polygon>
  )));
}

// Compact label for a resolved asset: "Alt/Code (OwnerCode)", else raw id.
export function assetLabel(rawId, info) {
  if (info && (info.displayName || info.assetCode)) {
    const owner = info.ownerCode ? ` (${info.ownerCode})` : '';
    return `${info.displayName || info.assetCode}${owner}`;
  }
  return rawId || '-';
}

export function formatTime(millis) {
  if (!millis) return '-';
  const m = moment(Number(millis));
  return m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss') : '-';
}

export function formatHm(millis) {
  if (!millis) return '-';
  const m = moment(Number(millis));
  return m.isValid() ? m.format('HH:mm:ss') : '-';
}

// Blue teardrop pin for the active playback position.
export function makePlaybackPinIcon() {
  return L.divIcon({
    className: 'passive-playback-pin',
    html: `
      <div style="position:relative;width:24px;height:24px;">
        <div style="position:absolute;left:1px;top:1px;width:22px;height:22px;background:#1890ff;border:2px solid #ffffff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 1px 4px rgba(0,0,0,0.4);"></div>
        <div style="position:absolute;left:8px;top:4px;width:10px;height:10px;color:#ffffff;font-weight:700;font-size:11px;line-height:10px;text-align:center;">&#9737;</div>
      </div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 24]
  });
}

// Greyed warning pin for "no GPS fix — last known position".
export function makeNoGpsPinIcon() {
  return L.divIcon({
    className: 'passive-playback-pin-nogps',
    html: `
      <div style="position:relative;width:24px;height:24px;">
        <div style="position:absolute;left:1px;top:1px;width:22px;height:22px;background:#8c8c8c;border:2px dashed #ffffff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 1px 4px rgba(0,0,0,0.4);"></div>
        <div style="position:absolute;left:7px;top:3px;width:12px;height:12px;color:#ffffff;font-weight:700;font-size:12px;line-height:12px;text-align:center;">&#9888;</div>
      </div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 24]
  });
}
